import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { components } from '@trackercop/contracts';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

type Baseline = components['schemas']['Baseline'];

interface ApproveBaselineModalProps {
  siteId: string;
  candidateCrawlId: string;
  onClose: () => void;
  onApproved?: (baseline: Baseline) => void;
}

const MAX_NOTES = 2048;

// Approve-a-crawl-as-baseline confirmation modal. User enters an
// optional reviewer note (plain text, 2048-char limit enforced
// client-side for UX — the server is authoritative), confirms, and we
// POST to /sites/:id/baseline. Approver identity is derived server-side
// from ctx.userId; never send approvedBy from the form. On success we
// invalidate the baseline + drift queries and close.
export function ApproveBaselineModal({
  siteId,
  candidateCrawlId,
  onClose,
  onApproved,
}: ApproveBaselineModalProps) {
  const [notes, setNotes] = useState('');
  const queryClient = useQueryClient();

  useEffect(() => {
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const mutation = useMutation({
    mutationFn: async (): Promise<Baseline> => {
      const { data, error } = await apiClient.POST('/sites/{siteId}/baseline', {
        params: { path: { siteId } },
        body: { crawlId: candidateCrawlId, notes: notes || undefined },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (baseline) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sites.baseline(siteId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.sites.drift(siteId) });
      queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'baseline', 'history'] });
      onApproved?.(baseline);
      onClose();
    },
  });

  const notesOverflowing = notes.length > MAX_NOTES;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      data-testid="approve-baseline-modal"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-md flex-col gap-3 rounded-md border bg-background p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold">Approve current crawl as baseline</h2>
        <p className="text-xs text-muted-foreground">
          Subsequent drift reports will compare against crawl{' '}
          <span className="font-mono">{candidateCrawlId.slice(0, 12)}…</span>. Any existing baseline is superseded.
        </p>
        <label className="flex flex-col gap-1 text-xs font-medium">
          Notes (optional)
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, MAX_NOTES))}
            placeholder="Reviewer context; appears in the audit record."
            data-testid="approve-baseline-notes"
            maxLength={MAX_NOTES}
          />
          <span className={`text-[10px] ${notesOverflowing ? 'text-destructive' : 'text-muted-foreground'}`}>
            {notes.length}/{MAX_NOTES}
          </span>
        </label>
        {mutation.isError && (
          <div className="rounded-sm border border-rose-300 bg-rose-50 p-2 text-xs text-rose-900">
            Approval failed. You may lack the `baseline:approve` permission.
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
            data-testid="approve-baseline-confirm"
          >
            {mutation.isPending ? 'Approving…' : 'Approve baseline'}
          </Button>
        </div>
      </div>
    </div>
  );
}
